/**
 * DevToolbar — unified fixed-position container for dev/debug controls.
 *
 * Renders as a pill in the bottom-right corner containing debug toggle,
 * renderer toggle, and any future dev controls as children.
 *
 * Draggable — click and hold to reposition. Position persists in localStorage.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { readStoredJson } from '../../lib/readStoredJson';
import { z } from 'zod';

const STORAGE_KEY = 'dev-toolbar-position';

const PositionSchema = z.object({
  right: z.number(),
  bottom: z.number(),
});
type Position = z.infer<typeof PositionSchema>;

export function loadPosition(): Position {
  // Default sits clear of the bottom-right corner, where floating controls
  // (the V1 dock FAB, since deleted) used to land on top of a `bottom: 4`
  // default, making this toolbar (and the MOCK/LIVE toggle on it) hard to
  // find or click. The offset is kept so existing stored positions still work.
  return readStoredJson(localStorage, STORAGE_KEY, PositionSchema, { right: 4, bottom: 84 });
}

function savePosition(pos: Position) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch { /* ignore */ }
}

export function DevToolbar({ children }: { children: React.ReactNode }) {
  const [pos, setPos] = useState(loadPosition);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, right: 0, bottom: 0 });
  const moved = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Only drag from the toolbar background, not buttons
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    dragging.current = true;
    moved.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY, right: pos.right, bottom: pos.bottom };
    ref.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = dragStart.current.x - e.clientX;
    const dy = dragStart.current.y - e.clientY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moved.current = true;
    const newRight = Math.max(0, dragStart.current.right + dx);
    const newBottom = Math.max(0, dragStart.current.bottom + dy);
    setPos({ right: newRight, bottom: newBottom });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    ref.current?.releasePointerCapture(e.pointerId);
    if (moved.current) {
      savePosition({ right: pos.right, bottom: pos.bottom });
    }
  }, [pos]);

  // Save on unmount if dragged
  useEffect(() => {
    return () => { if (moved.current) savePosition(pos); };
  }, [pos]);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="fixed z-40 flex items-center gap-1.5 bg-nightshade-900/90 border border-nightshade-700 rounded-full px-2 py-1 shadow-lg backdrop-blur-sm select-none"
      data-testid="dev-toolbar"
      style={{
        right: pos.right,
        bottom: pos.bottom,
        cursor: dragging.current ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
    >
      {children}
    </div>
  );
}

export function ToolbarButton({
  active,
  activeColor = 'amber',
  onClick,
  title,
  children,
}: {
  active: boolean;
  activeColor?: 'amber' | 'indigo' | 'green' | 'red';
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'bg-amber-500 text-white hover:bg-amber-600',
    indigo: 'bg-indigo-500 text-white hover:bg-indigo-600',
    green: 'bg-emerald-600 text-white hover:bg-emerald-700',
    red: 'bg-red-500 text-white hover:bg-red-600',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold transition-colors ${
        active ? colorMap[activeColor] : 'text-nightshade-400 hover:text-nightshade-200 hover:bg-nightshade-800'
      }`}
      title={title}
    >
      {children}
    </button>
  );
}
